import { SortDirection } from '@tanstack/react-table';
import styled, { css } from 'styled-components';

import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import { rem } from '@savgroup-front-common/primitive/helpers/units';

import { SafeMotionTr } from '../../../animation/SafeMotionTr';
import { fontStyle } from '../../../theme/helpers/fontStyle';

import { TABLE_ALIGN } from './DatatableCore.types';

export const $StyledTable = styled.table<{
  $withMinimalWidth: boolean;
}>`
  min-width: ${({ $withMinimalWidth }) =>
    $withMinimalWidth ? undefined : '100%'};
  border-color: ${({ theme }) => theme.newUI.variationColors.light3};
  border-spacing: unset;
  white-space: nowrap;
  width: 100%;
`;

export const $TableOrderContainer = styled.span``;
export const $TableContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
`;

interface CellMixinProps {
  canSortBy?: boolean;
  align?: TABLE_ALIGN | string;
  $size?: number;
  $minSize?: number;
  $maxSize?: number;
  sort?: false | SortDirection;
}

const cellMixin = css<CellMixinProps>`
  display: ${({ $size }) => ($size === 0 ? 'none' : undefined)};
  width: ${({ $size }) => ($size ? `${$size}px` : undefined)};
  min-width: ${({ $minSize }) =>
    $minSize && $minSize >= 0 ? `${$minSize}px` : undefined};
  max-width: ${({ $maxSize }) =>
    $maxSize && $maxSize >= 0 ? `${$maxSize}px` : undefined};
  padding: ${spacing('sm')};
  text-align: ${({ align }) => align || TABLE_ALIGN.LEFT};
  ${fontStyle('xs')};

  &:last-child {
    padding-right: 0;
  }

  &:hover ${$TableOrderContainer} > svg {
    animation-iteration-count: ${({ canSortBy }) =>
      canSortBy ? 'infinite' : null};
  }
`;

export const $StyledTd = styled.td<CellMixinProps>`
  ${cellMixin};
  font-weight: ${fontWeight('normal')};
  color: ${({ theme }) => theme.colors.mainTextColor};
  vertical-align: middle;
  ${fontStyle('sm')};
`;

const thHover = css`
  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.newUI.variationColors.light5};
  }
`;

export const $StyledTh = styled.th<CellMixinProps>`
  ${cellMixin};

  ${({ canSortBy }) => (canSortBy ? thHover : null)}
  ${({ canSortBy, sort }) => {
    if (canSortBy && !!sort) {
      return css`
        background-color: ${({ theme }) => theme.newUI.variationColors.light5};

        & * {
          font-weight: ${fontWeight('bold')};
        }
        & svg {
          color: ${({ theme }) => theme.colors.primary};
        }
      `;
    }
  }}
  
  border-bottom: ${({ theme }) => theme.newUI.inputBorderColors.default} ${rem(
    1,
  )} solid;
  padding-bottom: ${spacing(2.5)};
  user-select: none;

  font-weight: ${fontWeight('bold')};

  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  > span {
    white-space: break-spaces;
  }
  line-break: strict;
  white-space: nowrap;

  & > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${spacing('sm')};
  }
`;

export const $StyledHeader = styled.div`
  text-transform: uppercase;
  font-weight: ${fontWeight('bold')};
  min-width: 0;

  > svg {
    flex-shrink: 0;
  }
`;

export const $HeaderLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const $StyledTr = styled(SafeMotionTr)<{
  $isEven?: boolean;
  $isExpanded?: boolean;
  $isCliquable?: boolean;
  $isExpandedBackgroundEnabled?: boolean;
}>`
  position: relative;
  border-collapse: collapse;
  cursor: ${({ $isCliquable }) => ($isCliquable ? 'pointer' : 'default')};

  background-color: ${({
    theme,
    $isExpanded,
    $isExpandedBackgroundEnabled,
  }) => {
    if (!$isExpandedBackgroundEnabled) {
      return undefined;
    }

    if ($isExpanded) {
      return theme.newUI.variationColors.light6;
    }

    return undefined;
  }};
`;
export const $SubTr = styled(SafeMotionTr)<{
  $isCliquable?: boolean;
  $isExpandedBackgroundEnabled?: boolean;
}>`
  position: relative;
  border-collapse: collapse;
  cursor: ${({ $isCliquable }) => ($isCliquable ? 'pointer' : 'default')};

  background-color: ${({ theme, $isExpandedBackgroundEnabled }) => {
    if (!$isExpandedBackgroundEnabled) {
      return undefined;
    }

    return theme.newUI.variationColors.light6;
  }};
  padding: 0 ${spacing('lg')};
`;

export const $HeaderTr = styled.tr`
  position: relative;
  border-collapse: collapse;
`;

export const $FooterTr = styled.tr`
  position: relative;
  border-collapse: collapse;
`;

export const $StyledTFootTd = styled.td<CellMixinProps>`
  ${cellMixin};

  border-top: ${({ theme }) => theme.newUI.inputBorderColors.default} ${rem(1)}
    solid;
  padding-top: ${rem(10)};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  vertical-align: middle;
  font-size: ${({ theme }) => theme.fonts.size.small};
`;

export const $StyledTFoot = styled.tfoot`
  position: sticky;
  bottom: 0;
  z-index: 10;

  border-collapse: separate;
  background-color: ${({ theme }) => theme.systemColors.defaultBackground};
`;

export const $StyledTHead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 10;

  ${$StyledTr} {
    vertical-align: bottom;
  }

  border-collapse: separate;
  background-color: ${({ theme }) => theme.systemColors.defaultBackground};
`;

export const $StyledTBody = styled.tbody<{
  $height?: number;
}>`
  ${$StyledTr} {
    &:not(:first-child) ${$StyledTd} {
      border-top: ${({ theme }) => theme.newUI.inputBorderColors.default}
        ${spacing('px')} solid;
    }

    &:hover {
      background: ${({ theme }) => theme.newUI.variationColors.light6};
    }
  }
`;

export const $NoResultContainer = styled.div`
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
`;
